import { SendbirdError, UserOnlineState } from '@sendbird/chat';
import {
  GroupChannel,
  GroupChannelCreateParams,
  GroupChannelUpdateParams,
} from '@sendbird/chat/groupChannel';
import { FileMessage, UserMessage } from '@sendbird/chat/message';

import isJson from '~/ui/pages/Coordinate/helpers/isJson';
import getEnv from '~/utils/getEnv';
import { privateAxios } from '../requestsCollection';

import MessageTypes from '~/ui/pages/Coordinate/models/MessageTypes';

import sb from '~/services/sendbird/config';
import { ITeam } from '~/store/user/types';
import { IActTeamMemberAvailableList } from '../actTeamMember/types';
import { IResponse } from '../types';

export const removeMember = (channelUrl: string, userIds: string[]): IResponse<void> =>
  privateAxios({
    method: 'post',
    url: '/sendbird/remove-from-channel',
    data: { channelUrl, userIds },
  });

export const sbGetGroupChannel = (channelUrl: string): Promise<GroupChannel> =>
  new Promise<GroupChannel>((resolve, reject) => {
    if (sb) sb.groupChannel.getChannel(channelUrl).then(resolve).catch(reject);
  });

export const sbAddMembersGroupChannel = (
  members: string[],
  channelUrl: string,
): Promise<GroupChannel> =>
  new Promise<GroupChannel>((resolve, reject) => {
    sbGetGroupChannel(channelUrl)
      .then((channel: GroupChannel) => {
        channel.inviteWithUserIds(members).then(resolve).catch(reject);
      })
      .catch(error => reject(error));
  });

export const sbLeaveGroupChannel = (channelUrl: string, id: string): Promise<GroupChannel> =>
  new Promise<GroupChannel>((resolve, reject) => {
    sbGetGroupChannel(channelUrl)
      .then((channel: GroupChannel) => {
        const isDataJson = isJson(channel.data);
        const data = isDataJson ? JSON.parse(channel.data) : null;
        const archivedBy = (data?.archivedBy || [])?.filter((m: string) => m !== id);
        const params: GroupChannelUpdateParams = {
          data: data ? JSON.stringify({ ...data, archivedBy }) : '{}',
        };
        channel
          .updateChannel(params)
          .then(response =>
            channel
              .leave()
              .then(() => resolve(response))
              .catch(reject),
          )
          .catch(reject);
      })
      .catch(reject);
  });

export const sbArchiveGroupChannel = (channelUrl: string, id: string): Promise<GroupChannel> =>
  new Promise((resolve, reject) => {
    sbGetGroupChannel(channelUrl)
      .then((channel: GroupChannel) => {
        const isDataJson = isJson(channel.data);
        const data = isDataJson ? JSON.parse(channel.data) : null;
        const archivedBy = data?.archivedBy || [];
        const params: GroupChannelUpdateParams = {
          data: data ? JSON.stringify({ ...data, archivedBy: [...archivedBy, id] }) : '{}',
        };

        channel.updateChannel(params).then(resolve).catch(reject);
      })
      .catch(reject);
  });

export const sbMuteGroupChannel = (channelUrl: string, id: string): Promise<GroupChannel> =>
  new Promise((resolve, reject) => {
    sbGetGroupChannel(channelUrl)
      .then((channel: GroupChannel) => {
        const isDataJson = isJson(channel.data);
        const data = isDataJson ? JSON.parse(channel.data) : null;
        const mutedBy = data?.mutedBy || [];
        const params: GroupChannelUpdateParams = {
          data: data ? JSON.stringify({ ...data, mutedBy: [...mutedBy, id] }) : '{}',
        };

        channel.updateChannel(params).then(resolve).catch(reject);
      })
      .catch(reject);
  });

export const sbRenameGroupChannel = (channelUrl: string, name: string): Promise<GroupChannel> =>
  new Promise((resolve, reject) => {
    sbGetGroupChannel(channelUrl)
      .then((channel: GroupChannel) => {
        const params: GroupChannelUpdateParams = {
          name,
        };

        channel.updateChannel(params).then(resolve).catch(reject);
      })
      .catch(reject);
  });

export const sbUnArchiveGroupChannel = (channelUrl: string, id: string): Promise<GroupChannel> =>
  new Promise((resolve, reject) => {
    sbGetGroupChannel(channelUrl)
      .then((channel: GroupChannel) => {
        const isDataJson = isJson(channel.data);
        const data = isDataJson ? JSON.parse(channel.data) : null;
        const archivedBy = (data?.archivedBy || [])?.filter((m: string) => m !== id);
        const params: GroupChannelUpdateParams = {
          data: data ? JSON.stringify({ ...data, archivedBy }) : '{}',
        };
        channel.updateChannel(params).then(resolve).catch(reject);
      })
      .catch(reject);
  });

export const sbUnmuteGroupChannel = (channelUrl: string, id: string): Promise<GroupChannel> =>
  new Promise((resolve, reject) => {
    sbGetGroupChannel(channelUrl)
      .then((channel: GroupChannel) => {
        const isDataJson = isJson(channel.data);
        const data = isDataJson ? JSON.parse(channel.data) : null;
        const mutedBy = (data?.mutedBy || [])?.filter((m: string) => m !== id);
        const params: GroupChannelUpdateParams = {
          data: data ? JSON.stringify({ ...data, mutedBy }) : '{}',
        };
        channel.updateChannel(params).then(resolve).catch(reject);
      })
      .catch(reject);
  });

export const sbCreateGroupChannel = (
  inviteUserIdList: string[],
  clinicId: number,
  operators?: string[],
  clientId?: number,
  name?: string,
  direct?: boolean,
  teams?: ITeam[],
): Promise<GroupChannel> =>
  new Promise((resolve, reject) => {
    const params: GroupChannelCreateParams = {
      invitedUserIds: inviteUserIdList,
      operatorUserIds: operators,
      name,
      data: JSON.stringify({
        clientId: clientId || null,
        clinicId,
        direct,
        teams: teams || undefined,
      }),
    };

    sb.groupChannel
      .createChannel(params)
      .then(channel => {
        if (channel) {
          channel.createMetaData({
            clinicId: String(clinicId),
            clientId: clientId ? String(clientId) : undefined,
            teamIds: teams?.map(t => String(t.id)).join(',') || undefined,
            env: getEnv(),
          }); // for faster filtering/searching on BE
        }
        resolve(channel);
      })
      .catch(reject);
  });

export const sbUpdateOperators = (
  userIdList: string[],
  channelUrl: string,
): Promise<GroupChannel> => {
  const params: GroupChannelUpdateParams = {
    operatorUserIds: userIdList,
  };
  return new Promise((resolve, reject) => {
    sbGetGroupChannel(channelUrl)
      .then(channel => {
        channel.updateChannel(params).then(resolve).catch(reject);
      })
      .catch(error => {
        reject(error);
      });
  });
};

export const sbMarkAsReadGroupChannel = (channelUrl: string): Promise<GroupChannel> =>
  new Promise((resolve, reject) => {
    sbGetGroupChannel(channelUrl)
      .then((channel: GroupChannel) => {
        channel
          .markAsRead()
          .then(() => channel.refresh())
          .then(resolve)
          .catch(reject);
      })
      .catch((error: SendbirdError) => reject(error));
  });

export const sbGetOnlineMemberList = (channelUrl: string): Promise<string[]> =>
  new Promise((resolve, reject) => {
    sbGetGroupChannel(channelUrl)
      .then(channel => {
        channel
          .refresh()
          .then(c => {
            const list = c.members.reduce((array: string[], member) => {
              if (member.connectionStatus === UserOnlineState.ONLINE) {
                return [...array, member.userId];
              }
              return array;
            }, []);
            resolve(list);
          })
          .catch(reject);
      })
      .catch((error: SendbirdError) => reject(error));
  });

export const sbUpdateTextMessage = (
  channelUrl: string,
  messageId: number,
  message: string,
  prevMessage: UserMessage,
): Promise<UserMessage> =>
  new Promise((resolve, reject) => {
    const isDataJson = isJson(prevMessage.data);
    const dataObject = isDataJson ? JSON.parse(prevMessage.data) : null;
    const data = dataObject
      ? JSON.stringify({
          ...dataObject,
          editHistory: {
            ...dataObject.editHistory,
            [new Date().getTime()]: prevMessage.message,
          },
        })
      : JSON.stringify({
          editHistory: {
            [new Date().getTime()]: prevMessage.message,
          },
        });
    sbGetGroupChannel(channelUrl)
      .then(channel => {
        const params = {
          message,
          data,
        };
        channel.updateUserMessage(messageId, params).then(resolve).catch(reject);
      })
      .catch(error => {
        reject(error);
      });
  });

export const sbSendDocumentMessage = (
  channelUrl: string,
  file: File,
  type: MessageTypes,
  cb?: (reqId: string) => void,
): Promise<FileMessage> =>
  new Promise((resolve, reject) => {
    sbGetGroupChannel(channelUrl)
      .then(channel => {
        const params = {
          file,
          customType: type,
        };

        channel
          .sendFileMessage(params)
          .onPending(message => {
            cb?.(message?.reqId);
          })
          .onSucceeded(message => {
            resolve(message as FileMessage);
          })
          .onFailed(error => {
            reject(error);
          });
      })
      .catch(error => {
        reject(error);
      });
  });

export const sbCancelDocumentMessage = (channelUrl: string, reqId: string): Promise<boolean> =>
  new Promise((resolve, reject) => {
    sbGetGroupChannel(channelUrl)
      .then(channel => {
        channel.cancelUploadingFileMessage(reqId).then(resolve).catch(reject);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getRelatedTeamMembers = (
  clinicId: string,
  teamId: string,
  clientId: string,
): IResponse<IActTeamMemberAvailableList[]> =>
  privateAxios(
    {
      method: 'get',
      url: `clinic/${clinicId}/act-team/${teamId}/client/${clientId}/related-team-members`,
    },
    'v3',
  );

export default {};
